import React, { useState, useEffect, useRef } from "react";
import { FaSearch } from "react-icons/fa";
import { IoIosArrowUp, IoIosArrowDown, IoIosClose } from "react-icons/io";
import { MdWifiOff } from "react-icons/md";
import { isReallyOnline } from "../../../lib/isOnline";

export interface DropdownOption {
  value: string;
  label: string;
}

interface AsyncDropdownProps {
  placeholder?: string;
  onSelect: (option: DropdownOption) => void;
  onClear?: () => void;
  value?: string;
  disabled?: boolean;
  onSearch: (searchTerm: string) => Promise<DropdownOption[]>;
  minSearchLength?: number;
  searchDebounceMs?: number;
  emptyMessage?: string;
  loadingMessage?: string;
  allowClear?: boolean;
}

const AsyncDropdown: React.FC<AsyncDropdownProps> = ({
  placeholder = "Search...",
  onSelect,
  onClear,
  value,
  disabled = false,
  onSearch,
  minSearchLength = 2,
  searchDebounceMs = 500,
  emptyMessage = "No results found",
  loadingMessage = "Loading...",
  allowClear = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<"down" | "up">(
    "down"
  );
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<number | null>(null);

  const calculateDropdownPosition = () => {
    if (!dropdownRef.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setDropdownPosition("up");
    } else {
      setDropdownPosition("down");
    }
  };

  const handleSelect = (option: DropdownOption) => {
    setSelectedValue(option.label);
    setIsOpen(false);
    setSearchValue("");
    setOptions([]);
    onSelect(option);
  };

  const performSearch = async (searchTerm: string) => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Check minimum length
    if (searchTerm.length < minSearchLength) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    // Set loading state
    setIsLoading(true);

    // Debounce the API call
    searchTimeoutRef.current = window.setTimeout(async () => {
      try {
        // Check if online after debounce
        const online = await isReallyOnline();
        if (!online) {
          setIsOnline(false);
          setOptions([]);
          setIsLoading(false);
          return;
        }

        const results = await onSearch(searchTerm);
        setOptions(results || []);
      } catch (error) {
        console.error("Error fetching search results:", error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    }, searchDebounceMs);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    performSearch(newValue);
  };

  const handleDropdownToggle = () => {
    if (!disabled) {
      if (!isOpen) {
        calculateDropdownPosition();
      } else {
        // Reset when closing
        setIsOnline(true);
      }
      setIsOpen(!isOpen);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValue(null);
    setSearchValue("");
    setOptions([]);
    if (onClear) {
      onClear();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setSearchValue("");
      setOptions([]);
      setIsOnline(true); // Reset online state when closing
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", calculateDropdownPosition);
    window.addEventListener("scroll", calculateDropdownPosition, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", calculateDropdownPosition);
      window.removeEventListener("scroll", calculateDropdownPosition, true);

      // Cleanup timeout on unmount
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Update selected value when value prop changes
  useEffect(() => {
    if (value) {
      // For async dropdown, we might not have the label
      // You can optionally fetch it or just display the value
      setSelectedValue(value);
    } else {
      setSelectedValue(null);
    }
  }, [value]);

  const showMinLengthMessage =
    searchValue.length > 0 && searchValue.length < minSearchLength;
  const showEmptyMessage =
    !isLoading && searchValue.length >= minSearchLength && options.length === 0;
  const showOptions = !isLoading && options.length > 0;

  return (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className={`text-sm cursor-pointer py-3 px-4 flex justify-between items-center border border-gray-300 ${
          disabled ? "bg-gray-100" : "bg-white"
        } ${
          isOpen
            ? dropdownPosition === "up"
              ? "rounded-b-lg border-gray-500"
              : "rounded-t-lg border-gray-500"
            : "rounded-lg"
        }`}
        onClick={handleDropdownToggle}
        style={{
          color: selectedValue ? "#333333" : "#A2A2A2",
          borderBottomLeftRadius:
            isOpen && dropdownPosition === "down" ? "0px" : "8px",
          borderBottomRightRadius:
            isOpen && dropdownPosition === "down" ? "0px" : "8px",
          borderTopLeftRadius:
            isOpen && dropdownPosition === "up" ? "0px" : "8px",
          borderTopRightRadius:
            isOpen && dropdownPosition === "up" ? "0px" : "8px",
        }}
      >
        <span>{selectedValue ? selectedValue : placeholder}</span>
        <div className="flex items-center gap-1">
          {allowClear && selectedValue && !disabled && (
            <button
              onClick={handleClear}
              className="hover:bg-gray-200 rounded-full p-1 transition-colors"
            >
              <IoIosClose size={20} />
            </button>
          )}
          <span>
            {isOpen ? (
              dropdownPosition === "up" ? (
                <IoIosArrowDown />
              ) : (
                <IoIosArrowUp />
              )
            ) : (
              <IoIosArrowDown />
            )}
          </span>
        </div>
      </div>

      {isOpen && (
        <div
          ref={dropdownListRef}
          className={`absolute z-10 left-0 right-0 bg-white border border-gray-500 shadow-lg ${
            dropdownPosition === "up"
              ? "bottom-full rounded-t-lg border-b-0"
              : "top-full rounded-b-lg border-t-0"
          }`}
        >
          {!isOnline ? (
            /* Offline Message */
            <div className="p-4">
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <MdWifiOff className="text-4xl text-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    No Internet Connection
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Please check your connection and try again
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Search Input */}
              <div className="relative mb-2 p-2">
                <input
                  type="text"
                  value={searchValue}
                  disabled={disabled}
                  onChange={handleSearchChange}
                  placeholder={`Search (min ${minSearchLength} chars)...`}
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none"
                  style={{ color: "#333333" }}
                  autoFocus
                />
                <FaSearch className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>

              {/* Results */}
              <ul className="max-h-48 overflow-y-auto border-r border-b border-l border-gray-200">
                {isLoading && (
                  <li className="py-3 px-4 text-gray-500 text-sm text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
                      <span>{loadingMessage}</span>
                    </div>
                  </li>
                )}

                {showMinLengthMessage && (
                  <li className="py-3 px-4 text-gray-500 text-sm text-center">
                    Type at least {minSearchLength} characters to search
                  </li>
                )}

                {showEmptyMessage && (
                  <li className="py-3 px-4 text-gray-500 text-sm text-center">
                    {emptyMessage}
                  </li>
                )}

                {showOptions &&
                  options.map((option) => (
                    <li
                      key={option.value}
                      className="cursor-pointer py-3 px-4 hover:bg-gray-100 text-sm border-t border-[#f2f2f2]"
                      onClick={
                        disabled ? undefined : () => handleSelect(option)
                      }
                    >
                      {option.label}
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AsyncDropdown;
